import { json, type LoaderFunctionArgs } from '@remix-run/node';
import { type ShouldRevalidateFunction } from '@remix-run/react';
import { getTopNavigation } from '@modules/navigation';
import { getCombinedNotifications } from '~/modules/notifications/api/get-combined-notifications.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getShoppingCartSummary } from '@modules/ecommerce/api/get-shopping-cart-summary.server';
import { hasFeature, shouldNotRevalidateRoot } from '@modules/root';
import { getPermissions } from '~/modules/user';
import { PermissionEnum, SortDirection } from 'generated/graphql';
import { getSession } from '~/modules/root/util/session.server';
import AppPage from '@modules/root/page/AppPage';
import { toggleFeatures } from '~/modules/root/util/toggle-features';
import { getDesignBoardItems } from '~/modules-rocco/design-board/api/get-design-board-items.server';
import { getBottomContent } from '@modules/root/api/get-bottom-content.server';
import { getFilterDrawerCookieValue } from '~/modules/search/hook/use-filter-drawer-preference';
import { getMoodBoardPageIds } from '@modules-rocco/design-board/api/get-mood-board-page-ids.server';
import { getMonthlyUsers } from '~/modules/root/api/get-monthly-users.server';

/**
 * global catch and error boundary for all pages under __public
 * Child routes can still catch their own but if they don't we'll end up here
 * root.tsx will still render top nav etc
 */
export { ErrorBoundary, links } from '@modules/root/page/AppPage';

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { appDisplayMode, config, device, user, featureFlags } = context;
    const response = toggleFeatures(request);
    if (response) throw response;

    const buildHubEnabled = hasFeature(featureFlags, 'buildhub');

    const url = new URL(request.url);

    if (
        url.pathname.startsWith('/search') &&
        appDisplayMode !== 'desktop' &&
        !device.isBot
    ) {
        const [
            notifications,
            abTestConfig,
            session,
            designBoardItems,
            moodBoardPageIds,
            monthlyUsers,
        ] = await Promise.all([
            getCombinedNotifications(context),
            context.getABTests(),
            getSession(request),
            getDesignBoardItems(
                {
                    offset: 0,
                    limit: 2000,
                    sortBy: { LastEdited: SortDirection.Desc },
                },
                context
            ),
            getMoodBoardPageIds(context),
            getMonthlyUsers(context),
        ]);

        const bookConsultationEnabled = false;

        const ecommerceActiveEnabled = hasFeature(
            featureFlags,
            'ecommerce_active'
        );
        const ba2NavEnabled = hasFeature(featureFlags, 'ba_two_new_nav');

        const autoSubmitSuccess = session.getAutoSubmitSuccess();

        const availableForWorkEnabled = hasFeature(
            featureFlags,
            'available_for_work'
        );
        return json(
            {
                user,
                featureFlags,
                topNavigation: null,
                notifications,
                localisation: null,
                shoppingCartSummary: null,
                webSocketUrl: config.websocket.url,
                ecommerceActiveEnabled,
                ba2NavEnabled,
                bookConsultationEnabled,
                canApproveContent: getPermissions(
                    user,
                    PermissionEnum.BmContentApprove
                ),
                canCreateProfessional: getPermissions(
                    user,
                    PermissionEnum.BmProfessionalCreate
                ),
                toastMessage: session.getToastMessage(),
                autoSubmitSuccess,
                abTestConfig,
                showReseneSponsor: false,
                availableForWorkEnabled,
                professionalBookingLink: null,
                designBoardItems,
                moodBoardPageIds: buildHubEnabled
                    ? moodBoardPageIds
                    : { pageIDs: [], imageIDs: [] },
                bottomContent: null,
                filterDrawerCookieValue: getFilterDrawerCookieValue(request),
                monthlyUsers,
            },
            {
                headers: await session.getHeaders({
                    'Cache-Control': 'no-cache',
                }),
            }
        );
    }

    const [
        topNavigation,
        localisation,
        notifications,
        shoppingCartSummary,
        abTestConfig,
        session,

        designBoardItems,
        bottomContent,
        moodBoardPageIds,
        monthlyUsers,
    ] = await Promise.all([
        getTopNavigation(context),
        getPageLocalisation(args),
        getCombinedNotifications(context),
        getShoppingCartSummary(context),
        context.getABTests(),
        getSession(request),
        getDesignBoardItems(
            {
                offset: 0,
                limit: 2000,
                sortBy: { LastEdited: SortDirection.Desc },
            },
            context
        ),
        getBottomContent(context),
        getMoodBoardPageIds(context),
        getMonthlyUsers(context),
    ]);

    // Currently not being used.
    // const tabletEnabled = hasFeature(user.FeatureFlags, 'enable_tablet');

    const bookConsultationEnabled = false;

    const ecommerceActiveEnabled = hasFeature(featureFlags, 'ecommerce_active');
    const ba2NavEnabled = hasFeature(featureFlags, 'ba_two_new_nav');

    const autoSubmitSuccess = session.getAutoSubmitSuccess();

    const availableForWorkEnabled = hasFeature(
        featureFlags,
        'available_for_work'
    );

    return json(
        {
            user,
            featureFlags,
            topNavigation,
            localisation,
            notifications,
            shoppingCartSummary,
            webSocketUrl: config.websocket.url,
            ecommerceActiveEnabled,
            ba2NavEnabled,
            bookConsultationEnabled,
            canApproveContent: getPermissions(
                user,
                PermissionEnum.BmContentApprove
            ),
            canCreateProfessional: getPermissions(
                user,
                PermissionEnum.BmProfessionalCreate
            ),
            toastMessage: session.getToastMessage(),
            autoSubmitSuccess,
            abTestConfig,
            showReseneSponsor: false,
            availableForWorkEnabled,
            professionalBookingLink: null,
            designBoardItems,
            moodBoardPageIds: buildHubEnabled
                ? moodBoardPageIds
                : { pageIDs: [], imageIDs: [] },
            bottomContent,
            filterDrawerCookieValue: getFilterDrawerCookieValue(request),
            monthlyUsers,
        },
        {
            headers: await session.getHeaders({
                // Make sure user data is not cached.
                'Cache-Control': 'no-cache',
            }),
        }
    );
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
    formMethod,
    nextUrl,
}) => {
    if (
        // Temporary fix for login redirection issue.
        (!formMethod && nextUrl.pathname.startsWith('/search')) ||
        formMethod === 'GET'
    ) {
        // Do not revalidate on page navigation or URL search param change.
        return false;
    }
    if (shouldNotRevalidateRoot(formAction)) {
        return false;
    }
    return defaultShouldRevalidate;
};

const App = () => <AppPage />;

export default App;
